import React from 'react'
import {
  Box,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
} from '@mui/material'
import { Chip, CircularProgress } from '@material-ui/core'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { metricsTableCols } from 'util/ConstUtils'
import ImageNA from '../../images/ImageNA.svg'
import TableSearchBar from 'components/EnhancedTable/TableSearchBar'

const ProjectMetricsTable = ({
  buildRows,
  projectLoading,
  classes,
  titleCase,
  projectId,
  tcinStatusData,
}) => {
  const tcinProgressRows = buildRows()
  const [localPage, setLocalPage] = React.useState(1)
  const [localRowsPerPage, setLocalRowsPerPage] = React.useState(10)
  const [searchValue, setSearchValue] = React.useState('')
  const [expandedPipelineRows, setExpandedPipelineRows] = React.useState({})
  const searchText = String(searchValue || '')
    .trim()
    .toLowerCase()
  const filteredRows = searchText
    ? tcinProgressRows.filter((row) => {
        const fields = [row.tcin, row.dpci, row.brand, row.description]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase())
        return fields.some((value) => value.includes(searchText))
      })
    : tcinProgressRows
  const pageIndex = Math.max(localPage - 1, 0)
  const resolvedRowsPerPage = localRowsPerPage
  const totalRows = filteredRows.length
  const pagedRows = filteredRows.slice(
    pageIndex * resolvedRowsPerPage,
    pageIndex * resolvedRowsPerPage + resolvedRowsPerPage
  )

  React.useEffect(() => {
    if (totalRows === 0) {
      if (localPage !== 1) setLocalPage(1)
      return
    }
    if (pageIndex * resolvedRowsPerPage >= totalRows) {
      setLocalPage(1)
    }
  }, [pageIndex, resolvedRowsPerPage, totalRows, localPage])

  if (projectLoading) {
    return (
      <Box
        sx={{
          minHeight: 360,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <TableSearchBar
          classes={classes}
          enableMultiSearch={true}
          searchOpen={true}
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          onClear={() => setSearchValue('')}
        />
      </Box>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: 70,
          alignItems: 'center',
          margin: 10,
        }}
      >
        <Box
          component="div"
          sx={{
            fontSize: 16,
            color: 'text.primary',
            marginLeft: 1,
            alignSelf: 'center',
            fontWeight: 600,
          }}
        >
          {`${totalRows} Items`}
        </Box>
        <TablePagination
          component="div"
          count={totalRows}
          page={pageIndex}
          onPageChange={(_, newPage) =>
            (() => {
              const nextPage = newPage + 1
              setLocalPage(nextPage)
            })()
          }
          rowsPerPage={resolvedRowsPerPage}
          onRowsPerPageChange={(event) =>
            (() => {
              const nextRowsPerPage = parseInt(event.target.value, 10)
              setLocalRowsPerPage(nextRowsPerPage)
              setLocalPage(1)
            })()
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'baseline',
            color: 'text.primary',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                color: 'text.primary',
              },
            '& .MuiTablePagination-selectIcon, & .MuiTablePagination-actions button':
              {
                color: 'text.primary',
              },
            '& .MuiTablePagination-toolbar': {
              justifyContent: 'flex-end',
              alignItems: 'baseline',
              paddingRight: 0,
            },
          }}
        />
      </div>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table
          size="small"
          stickyHeader
          style={{
            '& .MuiTableCell-root': {
              color: '#333333',
              borderColor: 'rgba(0,0,0,0.08)',
            },
            '& .MuiTableCell-stickyHeader': {
              backgroundColor: '#ffffff',
              color: '#333333',
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: 'rgba(0,0,0,0.02)',
            },
          }}
        >
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                fontWeight: 500,
                paddingY: '12px',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
              },
            }}
          >
            <TableRow>
              {metricsTableCols?.map((label) => (
                <TableCell
                  key={label}
                  style={{
                    backgroundColor: '#fafafa',
                    ...(label === 'Description'
                      ? { width: 200, maxWidth: 200 }
                      : null),
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row) => {
              const hasRejected = row.rejectedImages.length > 0
              const isExpanded = expandedPipelineRows[row.tcin] || false
              return (
                <React.Fragment key={row.tcin}>
                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 700 }}>{row.tcin}</TableCell>
                    <TableCell>{row.dpci}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                      }}
                    >
                      {row.description}
                    </TableCell>
                    <TableCell>
                      {!row.previewUrl || row.previewUrl?.includes('null') ? (
                        <ImageNA
                          alt="No Image"
                          title="No Image"
                          className={classes.noImage}
                          key={row.tcin}
                          width={75}
                          height={75}
                        />
                      ) : (
                        <Box
                          component="img"
                          src={row.previewUrl}
                          alt={row.tcin}
                          sx={{
                            width: 75,
                            height: 75,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid rgba(255,255,255,0.15)',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{row.brand}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={titleCase(row.itemState)}
                        variant="outlined"
                        style={{
                          height: 20,
                          borderColor: 'rgba(0,0,0,0.15)',
                          color: 'black',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={titleCase(row.sampleStatus)}
                        variant="outlined"
                        style={{
                          height: 20,
                          borderColor: 'rgba(0,0,0,0.15)',
                          backgroundColor:
                            row.pegasusSampleCount === 0
                              ? 'none'
                              : row.pegasusSampleCount ===
                                  row.samplesReceivedCount
                                ? '#2e7d32'
                                : '#ed6c02',
                          color:
                            row.pegasusSampleCount === 0 ? 'black' : '#fff',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${row.imagesDone} Done`}
                        color={row.imagesDoneComplete ? 'success' : 'warning'}
                        variant="filled"
                        style={{
                          height: 20,
                          backgroundColor:
                            row.finalShotCount === 0 &&
                            row.photoBridgeImageCount === 0 &&
                            !row.pipelineAccepted
                              ? 'none'
                              : row.imagesDoneComplete
                                ? '#2e7d32'
                                : '#ed6c02',
                          color:
                            row.finalShotCount === 0 &&
                            row.photoBridgeImageCount === 0 &&
                            !row.pipelineAccepted
                              ? 'black'
                              : '#fff',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.pegasusDone ? (
                        <Chip
                          size="small"
                          label="✓"
                          color="success"
                          variant="filled"
                          style={{
                            fontSize: 11,
                            height: 20,
                            minWidth: 28,
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                          }}
                        />
                      ) : row.pegasusSampleCount > 0 ? (
                        <Tooltip title={`${row.sampleStatus}`}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: '#ed6c02',
                              color: '#fff',
                            }}
                          >
                            <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not yet started">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: 'rgba(0,0,0,0.08)',
                              color: 'rgba(0,0,0,0.4)',
                            }}
                          >
                            <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.photoBridgeDone ? (
                        <Chip
                          size="small"
                          label="✓"
                          color="success"
                          variant="filled"
                          style={{
                            fontSize: 11,
                            height: 20,
                            minWidth: 28,
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                          }}
                        />
                      ) : row.photoBridgeImageCount > 0 ? (
                        <Tooltip title={`${row.imagesDone} images`}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: '#ed6c02',
                              color: '#fff',
                            }}
                          >
                            <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not yet started">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: 'rgba(0,0,0,0.08)',
                              color: 'rgba(0,0,0,0.4)',
                            }}
                          >
                            <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const accepted = row.pipelineAccepted
                        const rejected = row.pipelineRejected
                        const total = accepted + rejected
                        const sc = row.finalShotCount || 0
                        const fullyAccepted =
                          sc > 0 && accepted >= sc && rejected === 0
                        const partiallyProcessed = total > 0 && !fullyAccepted
                        const tooltipText =
                          total > 0
                            ? `${accepted}/${sc} accepted · ${rejected} rejected`
                            : 'No images processed'

                        if (fullyAccepted) {
                          return (
                            <Tooltip title={tooltipText}>
                              <Chip
                                size="small"
                                label="✓"
                                color="success"
                                variant="filled"
                                style={{
                                  fontSize: 11,
                                  height: 20,
                                  minWidth: 28,
                                  backgroundColor: '#2e7d32',
                                  color: '#fff',
                                }}
                              />
                            </Tooltip>
                          )
                        }
                        if (hasRejected) {
                          return (
                            <Tooltip title={tooltipText}>
                              <Chip
                                size="small"
                                icon={
                                  <ErrorOutlineIcon
                                    style={{ fontSize: 14, color: '#fff' }}
                                  />
                                }
                                label=""
                                color="error"
                                variant="filled"
                                onClick={() =>
                                  setExpandedPipelineRows((prev) => ({
                                    ...prev,
                                    [row.tcin]: !prev[row.tcin],
                                  }))
                                }
                                deleteIcon={
                                  isExpanded ? (
                                    <KeyboardArrowUpIcon
                                      style={{ fontSize: 14, color: '#fff' }}
                                    />
                                  ) : (
                                    <KeyboardArrowDownIcon
                                      style={{ fontSize: 14, color: '#fff' }}
                                    />
                                  )
                                }
                                onDelete={() =>
                                  setExpandedPipelineRows((prev) => ({
                                    ...prev,
                                    [row.tcin]: !prev[row.tcin],
                                  }))
                                }
                                style={{
                                  fontSize: 11,
                                  height: 20,
                                  minWidth: 28,
                                  cursor: 'pointer',
                                  backgroundColor: '#cc0000',
                                  color: '#fff',
                                }}
                              />
                            </Tooltip>
                          )
                        }
                        if (partiallyProcessed) {
                          return (
                            <Tooltip title={tooltipText}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 28,
                                  height: 20,
                                  borderRadius: '10px',
                                  backgroundColor: '#ed6c02',
                                  color: '#fff',
                                }}
                              >
                                <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                              </Box>
                            </Tooltip>
                          )
                        }
                        return (
                          <Tooltip title="Not yet started">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 20,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(0,0,0,0.08)',
                                color: 'rgba(0,0,0,0.4)',
                              }}
                            >
                              <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                            </Box>
                          </Tooltip>
                        )
                      })()}
                    </TableCell>
                    <TableCell>
                      {row.assetHubDone ? (
                        <Chip
                          size="small"
                          label="✓"
                          color="success"
                          variant="filled"
                          style={{
                            fontSize: 11,
                            height: 20,
                            minWidth: 28,
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                          }}
                        />
                      ) : row.assetHubImageCount > 0 ? (
                        <Tooltip
                          title={`${row.assetHubImageCount}/${
                            row.finalShotCount || row.assetHubImageCount
                          } published`}
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: '#ed6c02',
                              color: '#fff',
                            }}
                          >
                            <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not yet started">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: 'rgba(0,0,0,0.08)',
                              color: 'rgba(0,0,0,0.4)',
                            }}
                          >
                            <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {!row.gargantuaExists ? (
                        <span style={{ color: 'rgba(0,0,0,0.35)' }}>—</span>
                      ) : row.gargantуaDone ? (
                        <Chip
                          size="small"
                          label="✓"
                          color="success"
                          variant="filled"
                          style={{
                            fontSize: 11,
                            height: 20,
                            minWidth: 28,
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                          }}
                        />
                      ) : row.gargantuaInProgress ? (
                        <Tooltip
                          title={`${row.gargantuaImageCount}/${
                            row.finalShotCount || row.gargantuaImageCount
                          } processed`}
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: '#ed6c02',
                              color: '#fff',
                            }}
                          >
                            <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not yet started">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: 'rgba(0,0,0,0.08)',
                              color: 'rgba(0,0,0,0.4)',
                            }}
                          >
                            <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.allComplete ? (
                        <Chip
                          size="small"
                          label="✓"
                          color="success"
                          variant="filled"
                          style={{
                            fontSize: 11,
                            height: 20,
                            minWidth: 28,
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                          }}
                        />
                      ) : hasRejected ? (
                        <Chip
                          size="small"
                          label="✗"
                          color="error"
                          variant="filled"
                          style={{
                            fontSize: 11,
                            height: 20,
                            minWidth: 28,
                            backgroundColor: '#cc0000',
                            color: '#fff',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 20,
                            borderRadius: '10px',
                            backgroundColor: '#ed6c02',
                            color: '#fff',
                          }}
                        >
                          <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  {hasRejected && (
                    <TableRow>
                      <TableCell
                        colSpan={14}
                        sx={{
                          py: 0,
                          borderBottom: isExpanded ? undefined : 'none',
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, px: 2 }}>
                            <Stack spacing={0.5}>
                              {row.rejectedImages.map((rImg, idx) => (
                                <Stack
                                  key={idx}
                                  direction="row"
                                  alignItems="center"
                                  spacing={1.5}
                                >
                                  <ErrorOutlineIcon
                                    style={{ fontSize: 14, color: '#d32f2f' }}
                                  />
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: 'black',
                                    }}
                                  >
                                    {rImg.name}
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={
                                      rImg.type === 'main' ? 'Main' : 'Alt'
                                    }
                                    variant="outlined"
                                    style={{
                                      fontSize: 10,
                                      height: 18,
                                      borderColor: 'rgba(0,0,0,0.15)',
                                      color: 'black',
                                    }}
                                  />
                                  {rImg.label && (
                                    <Tooltip
                                      title={rImg.rawMessage || rImg.label}
                                      arrow
                                      placement="top"
                                    >
                                      <Chip
                                        size="small"
                                        label={rImg.label}
                                        color="error"
                                        variant="filled"
                                        style={{
                                          fontSize: 10,
                                          height: 18,
                                          fontWeight: 700,
                                          cursor: 'pointer',
                                          backgroundColor: '#cc0000',
                                          color: '#fff',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalRows}
        page={pageIndex}
        onPageChange={(_, newPage) => {
          const nextPage = newPage + 1
          setLocalPage(nextPage)
        }}
        rowsPerPage={resolvedRowsPerPage}
        onRowsPerPageChange={(event) =>
          (() => {
            const nextRowsPerPage = parseInt(event.target.value, 10)
            setLocalRowsPerPage(nextRowsPerPage)
            setLocalPage(1)
          })()
        }
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'baseline',
          color: 'text.primary',
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
            {
              color: 'text.primary',
            },
          '& .MuiTablePagination-selectIcon, & .MuiTablePagination-actions button':
            {
              color: 'text.primary',
            },
          '& .MuiTablePagination-toolbar': {
            justifyContent: 'flex-end',
            alignItems: 'baseline',
            paddingRight: 0,
          },
        }}
      />
    </Box>
  )
}

export default React.memo(ProjectMetricsTable)
